import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../components/auth/AuthModule';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  Grid,
  Card,
  CardContent,
  Skeleton
} from '@mui/material';
import {
  Payment as PaymentIcon,
  MoneyOff as MoneyOffIcon,
  Check as CheckIcon,
  Event as EventIcon,
  Build as BuildIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import { payments } from '../../utils/api';
import { formatDistanceToNow } from 'date-fns';
import { AnimatePresence } from 'framer-motion';
import { es, ca } from 'date-fns/locale';

const PaymentsPage = () => {
  const { t, i18n } = useTranslation(['payments', 'common']);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [debtsOwed, setDebtsOwed] = useState([]);
  const [debtsToCollect, setDebtsToCollect] = useState([]);
  const [allDebts, setAllDebts] = useState([]);
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentNote, setPaymentNote] = useState('');
  const [showPaid, setShowPaid] = useState(false);
  const [error, setError] = useState(null);
  const [problematicEntries, setProblematicEntries] = useState(null);
  const [showTroubleshooting, setShowTroubleshooting] = useState(false);

  // Move isValidDebt to component scope
  const isValidDebt = (debt) => {
    return debt && 
           debt.debtor && 
           debt.creditor && 
           debt.debtor._id && 
           debt.creditor._id && 
           typeof debt.amount === 'number';
  };

  useEffect(() => {
    fetchDebts();
  }, [showPaid]);

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'es':
        return es;
      case 'ca':
        return ca;
      default:
        return undefined;
    }
  };

  const fetchDebts = async () => {
    try {
      setLoading(true);
      setError(null);
      setProblematicEntries(null);
      
      // Fetch filtered debts based on showPaid toggle
      const response = await payments.getAllDebts(showPaid);
      
      // Always fetch all debts for summary calculation
      let allDebtsResponse;
      if (!showPaid) {
        allDebtsResponse = await payments.getAllDebts(true);
      } else {
        allDebtsResponse = response;
      }
      
      // Filter and clean the debts arrays
      const cleanAndFilterDebts = (debtsArray) => {
        if (!Array.isArray(debtsArray?.debts)) {
          console.warn('Invalid debts array received:', debtsArray);
          return [];
        }

        return debtsArray.debts.filter(debt => {
          if (!isValidDebt(debt)) {
            console.warn('Invalid debt object found:', debt);
            return false;
          }
          // Filter out self-debts with 0€ amount
          return !(debt.debtor._id === debt.creditor._id && debt.amount === 0);
        });
      };

      // Process current debts
      const filteredDebts = cleanAndFilterDebts(response);
      
      // Process all debts (for summary)
      const allFilteredDebts = cleanAndFilterDebts(allDebtsResponse);
      
      // Store problematic entries if any
      if (response.problematicEntries) {
        setProblematicEntries(response.problematicEntries);
      }

      // Safely filter debts by user
      const filterDebtsByUser = (debts, filterType) => {
        if (!Array.isArray(debts) || !user?._id) return [];
        
        return debts.filter(debt => {
          try {
            if (filterType === 'owed') {
              return debt.debtor._id === user._id;
            } else {
              return debt.creditor._id === user._id;
            }
          } catch (err) {
            console.warn(`Error filtering debt:`, err);
            return false;
          }
        });
      };

      const owed = filterDebtsByUser(filteredDebts, 'owed');
      const toCollect = filterDebtsByUser(filteredDebts, 'toCollect');
      
      setDebtsOwed(owed);
      setDebtsToCollect(toCollect);
      setAllDebts(allFilteredDebts);

    } catch (err) {
      console.error('Error fetching debts:', err);
      // Set empty arrays to prevent undefined errors in the UI
      setDebtsOwed([]);
      setDebtsToCollect([]);
      setAllDebts([]);
      setError(t('errors.fetchingDebts'));
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handlePaymentClick = (debt, event) => {
    event.stopPropagation(); // Prevent card click when clicking the pay button
    setSelectedDebt(debt);
    setPaymentDialogOpen(true);
  };

  const handleDebtCardClick = (debt) => {
    try {
      if (!debt?.type || !debt?.id) return;
      
      const [type, id] = debt.id.split('-');
      if (!id) return;

      if (debt.type === 'event') {
        navigate(`/events/${id}`);
      } else if (debt.type === 'workshop') {
        navigate(`/workshop/tasks/${id}`);
      }
    } catch (err) {
      console.warn('Error handling debt card click:', err);
    }
  };

  const handlePaymentSubmit = async () => {
    try {
      setLoading(true);
      await payments.markAsPaid(selectedDebt.id, {
        notes: paymentNote,
        paidAt: new Date().toISOString()
      });
      
      setPaymentDialogOpen(false);
      setPaymentNote('');
      setSelectedDebt(null);
      
      // Always fetch both filtered and all debts after payment confirmation
      fetchDebts();
    } catch (err) {
      console.error('Error marking debt as paid:', err);
      setError(t('errors.markingAsPaid'));
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const calculateTotals = (debts) => {
    if (!Array.isArray(debts)) return 0;
    return debts.reduce((total, debt) => {
      if (!debt || typeof debt.amount !== 'number' || !debt.isPaid === undefined) {
        return total;
      }
      return total + (debt.isPaid ? 0 : debt.amount);
    }, 0);
  };

  const calculatePaidTotals = (debts) => {
    if (!Array.isArray(debts)) return 0;
    return debts.reduce((total, debt) => {
      try {
        // Only count debts that are paid AND relevant to the current user
        if (debt?.isPaid && 
            debt?.amount && 
            debt?.debtor?._id && 
            debt?.creditor?._id && 
            user?._id && 
            (debt.debtor._id === user._id || debt.creditor._id === user._id)) {
          return total + debt.amount;
        }
      } catch (err) {
        console.warn('Error calculating paid total for debt:', err);
      }
      return total;
    }, 0);
  };

  const getDebtSourceIcon = (type) => {
    switch (type) {
      case 'event':
        return <EventIcon />;
      case 'workshop':
        return <BuildIcon />;
      default:
        return <PaymentIcon />;
    }
  };

  const renderSkeletonSummaryCards = () => (
    <Box 
      sx={{ 
        mb: 3,
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        mx: { xs: -2, sm: 0 },
        px: { xs: 2, sm: 0 }
      }}
    >
      <Box 
        sx={{ 
          display: 'flex',
          gap: 2,
          pb: 1,
          minWidth: { xs: 'fit-content', sm: '100%' }
        }}
      >
        {[1, 2, 3, 4].map((index) => (
          <Card key={index} sx={{ 
            minWidth: { xs: '75vw', sm: 'auto' },
            flex: { sm: 1 }
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" width={120} height={32} />
              </Box>
              <Skeleton variant="text" width={150} height={48} sx={{ mt: 1 }} />
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );

  const renderSkeletonDebtItem = () => (
    <Paper elevation={1} sx={{ mb: 2 }}>
      <ListItem
        sx={{
          borderLeft: 6,
          borderColor: 'action.disabled',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 2, sm: 0 },
          pr: { xs: 2, sm: 9 }
        }}
      >
        <Box sx={{ 
          display: 'flex',
          alignItems: 'flex-start',
          width: '100%'
        }}>
          <Box sx={{ mr: 2, mt: 0.5 }}>
            <Skeleton variant="circular" width={24} height={24} />
          </Box>
          <ListItemText
            primary={
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Skeleton variant="text" width="60%" height={24} sx={{ flex: 1 }} />
                <Skeleton variant="rounded" width={80} height={24} />
              </Box>
            }
            secondary={
              <Box>
                <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                  <Skeleton variant="circular" width={24} height={24} />
                  <Skeleton variant="text" width="50%" height={20} />
                </Box>
                <Skeleton variant="text" width="30%" height={16} sx={{ mt: 0.5 }} />
              </Box>
            }
          />
        </Box>
        <Box sx={{ 
          display: { xs: 'flex', sm: 'none' },
          width: '100%'
        }}>
          <Skeleton variant="rounded" width="100%" height={36} />
        </Box>
        <Box sx={{ 
          display: { xs: 'none', sm: 'block' },
          position: 'absolute',
          right: 16
        }}>
          <Skeleton variant="rounded" width={120} height={36} />
        </Box>
      </ListItem>
    </Paper>
  );

  const renderSummaryCards = () => {
    const totalOwed = calculateTotals(debtsOwed);
    const totalToCollect = calculateTotals(debtsToCollect);
    const totalPaid = calculatePaidTotals(allDebts);
    const netBalance = totalToCollect - totalOwed;

    // Determine if we're loading the full paid data
    const isPaidLoading = loading && allDebts.length === 0;

    return (
      <Box 
        sx={{ 
          mb: 3,
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          mx: { xs: -2, sm: 0 },
          px: { xs: 2, sm: 0 }
        }}
      >
        <Box 
          sx={{ 
            display: 'flex',
            gap: 2,
            pb: 1,
            minWidth: { xs: 'fit-content', sm: '100%' }
          }}
        >
          <Paper 
            elevation={0}
            sx={{ 
              minWidth: { xs: '75vw', sm: 'auto' },
              flex: { sm: 1 },
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderLeft: 3,
              borderLeftColor: 'error.main',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Box sx={{ p: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <ArrowUpwardIcon color="error" />
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  {t('totalOwed')}
                </Typography>
              </Box>
              <Typography variant="h4" color="error" sx={{ fontWeight: 'bold' }}>
                {formatAmount(totalOwed)}
              </Typography>
            </Box>
          </Paper>

          <Paper 
            elevation={0}
            sx={{ 
              minWidth: { xs: '75vw', sm: 'auto' },
              flex: { sm: 1 },
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderLeft: 3,
              borderLeftColor: 'success.main',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Box sx={{ p: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <ArrowDownwardIcon color="success" />
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  {t('totalToCollect')}
                </Typography>
              </Box>
              <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                {formatAmount(totalToCollect)}
              </Typography>
            </Box>
          </Paper>

          <Paper 
            elevation={0}
            sx={{ 
              minWidth: { xs: '75vw', sm: 'auto' },
              flex: { sm: 1 },
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderLeft: 3,
              borderLeftColor: 'success.main',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Box sx={{ p: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CheckIcon color="success" />
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  {t('totalPaid')}
                </Typography>
              </Box>
              {isPaidLoading ? (
                <Box display="flex" alignItems="center" height={48}>
                  <CircularProgress size={24} color="success" />
                </Box>
              ) : (
                <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                  {formatAmount(totalPaid)}
                </Typography>
              )}
            </Box>
          </Paper>

          <Paper 
            elevation={0}
            sx={{ 
              minWidth: { xs: '75vw', sm: 'auto' },
              flex: { sm: 1 },
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderLeft: 3,
              borderLeftColor: netBalance >= 0 ? 'success.main' : 'error.main',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Box sx={{ p: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PaymentIcon color={netBalance >= 0 ? "success" : "error"} />
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  {t('netBalance')}
                </Typography>
              </Box>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 'bold',
                  color: netBalance >= 0 ? 'success.main' : 'error.main'
                }}
              >
                {formatAmount(netBalance)}
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  };

  const renderDebtList = (debts) => {
    if (!Array.isArray(debts)) {
      console.warn('Invalid debts array in renderDebtList:', debts);
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          {t('errors.invalidData')}
        </Alert>
      );
    }

    if (debts.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          {tabValue === 0 ? t('noDebtsOwed') : t('noDebtsToCollect')}
        </Alert>
      );
    }

    return (
      <List sx={{ p: 0 }}>
        {debts.map((debt) => {
          // Skip rendering invalid debts
          if (!isValidDebt(debt)) {
            console.warn('Invalid debt object in list:', debt);
            return null;
          }

          return (
            <Paper 
              key={debt.id} 
              elevation={0} 
              sx={{ 
                mb: 2,
                cursor: 'pointer',
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                '&:hover': {
                  bgcolor: 'action.hover',
                  transition: 'all 0.2s ease-in-out'
                }
              }}
              onClick={() => handleDebtCardClick(debt)}
            >
              <ListItem
                sx={{
                  borderLeft: 3,
                  borderColor: debt.isPaid ? 'success.main' : 'warning.main',
                  py: 2,
                  px: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2
                }}
              >
                <Box sx={{ 
                  display: 'flex',
                  width: '100%',
                  gap: 2,
                  position: 'relative'
                }}>
                  <Avatar
                    src={tabValue === 0 ? debt.creditor.imageUrl : debt.debtor.imageUrl}
                    sx={{ 
                      width: 40, 
                      height: 40,
                      boxShadow: 1
                    }}
                  >
                    {(tabValue === 0 ? debt.creditor.name : debt.debtor.name)?.[0]}
                  </Avatar>
                  
                  <Box sx={{ flex: 1 }}>
                    <Box display="flex" alignItems="center">
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {tabValue === 0 
                          ? t('youOwe', { name: debt.creditor.name || debt.creditor.username })
                          : t('owesYou', { name: debt.debtor.name || debt.debtor.username })
                        }
                      </Typography>
                    </Box>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      color: 'text.secondary',
                      pr: { xs: '90px', sm: 0 }  // Increased space for the amount on mobile
                    }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: { xs: 'calc(100% - 85px)', sm: '100%' }  // Ensure there's room for the amount on mobile
                        }}
                      >
                        {debt.title.length > 31 ? `${debt.title.slice(0, 31)}...` : debt.title}
                      </Typography>
                    </Box>

                    {/* Cost breakdown - only visible on desktop */}
                    {debt.type === 'workshop' && (
                      <Box sx={{ 
                        display: { xs: 'none', sm: 'flex' }, 
                        gap: 1, 
                        alignItems: 'center',
                        mt: 0.5,
                        color: 'text.secondary' 
                      }}>
                        {debt.repairCost > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            {t('repairCost')}: {formatAmount(debt.repairCost)}
                          </Typography>
                        )}
                        {debt.labourCost > 0 && (
                          <>
                            {debt.repairCost > 0 && <Typography variant="caption">•</Typography>}
                            <Typography variant="caption" color="text.secondary">
                              {t('labourCost')}: {formatAmount(debt.labourCost)}
                            </Typography>
                          </>
                        )}
                      </Box>
                    )}

                    <Box 
                      display="flex" 
                      alignItems="center" 
                      gap={2}
                      sx={{ 
                        display: { xs: 'none', sm: 'flex' },
                        mt: 0.5
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {debt.type === 'event' ? t('relatedToEvent') : t('relatedToWorkshop')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        •
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDistanceToNow(new Date(debt.createdAt), { 
                          addSuffix: true,
                          locale: getDateLocale()
                        })}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Mobile amount display */}
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      display: { xs: 'flex', sm: 'none' },
                      position: 'absolute',
                      right: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontWeight: 'bold',
                      color: debt.isPaid ? 'success.main' : 'warning.main',
                      bgcolor: 'background.paper',
                      pl: 1,  // Add left padding for separation
                      fontSize: '1rem'  // Slightly larger for better visibility
                    }}
                  >
                    {formatAmount(debt.amount)}
                  </Typography>

                  {/* Desktop amount and pay button */}
                  <Box sx={{
                    display: { xs: 'none', sm: 'flex' },
                    alignItems: 'center',
                    gap: 2,
                    alignSelf: 'center'
                  }}>
                    <Chip
                      size="medium"
                      label={formatAmount(debt.amount)}
                      color={debt.isPaid ? "success" : "warning"}
                      sx={{ 
                        fontWeight: 'bold',
                        px: 1,
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                    
                    {!debt.isPaid && (
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        sx={{ 
                          minWidth: 'auto',
                          px: 2,
                          borderRadius: 2,
                          whiteSpace: 'nowrap'
                        }}
                        onClick={(e) => handlePaymentClick(debt, e)}
                        startIcon={<PaymentIcon />}
                      >
                        {tabValue === 0 ? t('payNow') : t('markAsPaid')}
                      </Button>
                    )}
                  </Box>
                </Box>

                {/* Mobile pay button */}
                <Box sx={{
                  display: { xs: 'flex', sm: 'none' },
                  width: '100%'
                }}>
                  {!debt.isPaid && (
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      sx={{ 
                        borderRadius: 2,
                        whiteSpace: 'nowrap'
                      }}
                      onClick={(e) => handlePaymentClick(debt, e)}
                      startIcon={<PaymentIcon />}
                    >
                      {tabValue === 0 ? t('payNow') : t('markAsPaid')}
                    </Button>
                  )}
                </Box>
              </ListItem>
            </Paper>
          );
        })}
      </List>
    );
  };
  const renderProblematicEntries = () => {
    // Only display troubleshooting section for admins
    if (!user?.isAdmin || !problematicEntries || (!problematicEntries.events.length && !problematicEntries.tasks.length)) {
      return null;
    }

    return (
      <Box sx={{ mt: 4 }}>
        <Paper sx={{ p: 2, bgcolor: 'warning.light' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" color="warning.dark">
              {t('troubleshooting.title')}
            </Typography>
            <Button
              variant="outlined"
              color="warning"
              onClick={() => setShowTroubleshooting(!showTroubleshooting)}
            >
              {showTroubleshooting ? t('troubleshooting.hide') : t('troubleshooting.show')}
            </Button>
          </Box>

          {showTroubleshooting && (
            <>
              {problematicEntries.events.length > 0 && (
                <Box mb={2}>
                  <Typography variant="subtitle1" color="warning.dark" gutterBottom>
                    {t('troubleshooting.eventIssues')}
                  </Typography>
                  {problematicEntries.events.map((entry, index) => (
                    <Paper key={`${entry.id}-${index}`} sx={{ p: 2, mb: 1, bgcolor: 'background.paper' }}>
                      <Typography variant="subtitle2">
                        {entry.title} ({entry.type})
                      </Typography>
                      <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                        {Object.entries(entry.issues).map(([key, value]) => (
                          <Typography component="li" variant="body2" key={key}>
                            {t(`troubleshooting.issues.${key}`)}: {value?.toString()}
                          </Typography>
                        ))}
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}

              {problematicEntries.tasks.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" color="warning.dark" gutterBottom>
                    {t('troubleshooting.taskIssues')}
                  </Typography>
                  {problematicEntries.tasks.map((entry, index) => (
                    <Paper key={`${entry.id}-${index}`} sx={{ p: 2, mb: 1, bgcolor: 'background.paper' }}>
                      <Typography variant="subtitle2">
                        {entry.title} ({entry.type})
                      </Typography>
                      <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                        {Object.entries(entry.issues).map(([key, value]) => (
                          <Typography component="li" variant="body2" key={key}>
                            {t(`troubleshooting.issues.${key}`)}: {value?.toString()}
                          </Typography>
                        ))}
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </>
          )}
        </Paper>
      </Box>
    );
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {loading ? (
          <>
            {renderSkeletonSummaryCards()}

            <Box sx={{ mb: 3 }}>
              <Paper sx={{ mb: 2 }}>
                <Skeleton variant="rectangular" height={48} />
              </Paper>

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Skeleton variant="rounded" width={150} height={40} />
                <Box>
                  <Skeleton variant="circular" width={32} height={32} sx={{ display: 'inline-block', mr: 1 }} />
                  <Skeleton variant="circular" width={32} height={32} sx={{ display: 'inline-block' }} />
                </Box>
              </Box>
            </Box>

            {[1, 2, 3, 4].map((item) => (
              <React.Fragment key={item}>
                {renderSkeletonDebtItem()}
              </React.Fragment>
            ))}
          </>
        ) : (
          <>
            {renderSummaryCards()}

            <Box sx={{ mb: 3 }}>
              <Paper sx={{ mb: 2 }}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="fullWidth"
                >
                  <Tab 
                    icon={<MoneyOffIcon />} 
                    label={t('debtsOwed')} 
                    iconPosition="start"
                  />
                  <Tab 
                    icon={<PaymentIcon />} 
                    label={t('debtsToCollect')} 
                    iconPosition="start"
                  />
                </Tabs>
              </Paper>

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <FormControlLabel
                  control={
                    <Switch
                      checked={showPaid}
                      onChange={(e) => setShowPaid(e.target.checked)}
                    />
                  }
                  label={t('showPaidDebts')}
                />
                
              </Box>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box>
              {tabValue === 0 ? renderDebtList(debtsOwed) : renderDebtList(debtsToCollect)}
            </Box>

            {renderProblematicEntries()}
          </>
        )}
      </Box>

      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)}>
        <DialogTitle>
          {tabValue === 0 ? t('confirmPayment') : t('confirmPaymentReceived')}
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {tabValue === 0
              ? t('confirmPaymentText', {
                  amount: selectedDebt ? formatAmount(selectedDebt.amount) : '',
                  name: selectedDebt?.creditor.name || selectedDebt?.creditor.username
                })
              : t('confirmPaymentReceivedText', {
                  amount: selectedDebt ? formatAmount(selectedDebt.amount) : '',
                  name: selectedDebt?.debtor.name || selectedDebt?.debtor.username
                })
            }
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            margin="normal"
            label={t('paymentNote')}
            value={paymentNote}
            onChange={(e) => setPaymentNote(e.target.value)}
            placeholder={t('paymentNotePlaceholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handlePaymentSubmit}
            startIcon={<CheckIcon />}
          >
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PaymentsPage; 